// 9fbef606107a605d69c0edbcd8029e5d
import { loadLayoutData, loadModulesData } from '@tcp/core/src/reduxStore/actions';
import { call, put, takeLatest, select, fork, join, delay } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import { getUnbxdXappConfigs } from '@tcp/core/src/components/features/browse/ProductListing/container/ProductListing.saga';
import MAX_PRODUCT_PART_NUMBER_ALLOWED from '@tcp/core/src/constants/numeric.constants';
import processHelperUtil, {
  updateColorFitsSizesMapWithSFCCInventoryData,
  updateRawDataWithSFCCInventoryData,
} from '@tcp/core/src/services/abstractors/productListing/ProductDetail.util';
import { getInYourBagItems } from '@tcp/core/src/services/abstractors/bundleProduct/BundleProduct';
import { newBreadcrumbs } from '@tcp/core/src/components/features/browse/ProductDetail/container/ProductDetail.selectors';
import CARTPAGE_CONSTANTS from '@tcp/core/src/components/features/CnC/CartItemTile/CartItemTile.constants';
import BUNDLEPRODUCT_CONSTANTS from './BundleProduct.constants';
import {
  setProductDetails,
  setBundleDetails,
  setDepartmentBundleProducts,
  setLoadingState,
  setPageUrl,
  setInYourBagCount,
  updateBundleProductDetails,
} from './BundleProduct.actions';
import {
  getIsPdpServiceEnabled,
  getABTestPdpService,
  getIsPJPlannerExperienceEnabled,
  getIsExtendedInventoryPromoMessagingEnabled,
} from '../../../../../reduxStore/selectors/session.selectors';
import getProductInfoById, {
  layoutResolver,
  sfccInventoryApi,
} from '../../../../../services/abstractors/productListing/productDetail';
import { getDefaultWishList } from '../../Favorites/container/Favorites.saga';
import getBundleProductsDetails from '../../../../../services/abstractors/productListing/bundleProducts';
import logger from '../../../../../utils/loggerInstance';
import { setSlpLoadingState } from '../../SearchDetail/container/SearchDetail.actions';
import { getIsBundle } from './BundleProduct.selectors';
import { getIsOutfit } from '../../OutfitDetails/container/OutfitDetails.selectors';
import { getBasketId, resolveCall } from '../../../../../utils';

const getProductsLength = (products = []) => {
  return products?.length || 0;
};

function* getDepartmentBundleProducts(bundleDetails) {
  const departmentBundleProducts = {};
  bundleDetails?.forEach((bundle) => {
    const departments = bundle?.products?.departmentName;
    departments?.forEach((department) => {
      if (department in departmentBundleProducts) {
        const bundleProductsObj = { ...bundle.products, currentDepartmentName: department };
        departmentBundleProducts[department].push(bundleProductsObj);
      } else {
        const bundleProductsObj = { ...bundle.products, currentDepartmentName: department };
        departmentBundleProducts[department] = [bundleProductsObj];
      }
    });
  });

  yield put(setDepartmentBundleProducts(departmentBundleProducts));
}

function* callSFCCInventoryAPI(res) {
  let taskData = [];
  const productsLength = getProductsLength(res);

  try {
    let INITIAL_VALUE = 0;
    let MAX_VALUE = MAX_PRODUCT_PART_NUMBER_ALLOWED;
    let finalProductsList = [];
    for (let i = 0; i < productsLength; i += 1) {
      const products = res?.[i]?.products;

      const allProducts = products?.allProductPartNos;
      if (products?.getNewMultiPackAllColor?.length) {
        products.getNewMultiPackAllColor.forEach((item) => {
          if (item?.prodpartno) {
            allProducts.push(item?.prodpartno);
          }
        });
      }
      finalProductsList = [...finalProductsList, ...new Set(allProducts)];
    }
    let finalProductsListLength = finalProductsList?.length || 0;
    // To make multiple calls when allProductPartNosLength is greater that maximum allowed productPartNo value
    while (finalProductsListLength > 0) {
      const sfccTaskData = yield fork(
        sfccInventoryApi,
        finalProductsList?.slice(INITIAL_VALUE, MAX_VALUE)
      );
      if (sfccTaskData) {
        taskData = [...taskData, sfccTaskData];
      }
      // Set the INITIAL_VALUE to max value, add maximum allowed prodPartNo to MAX_VALUE, and reduce the allProductParNos length by maximum allowed prodPartNo value for next iteration
      INITIAL_VALUE = MAX_VALUE;
      MAX_VALUE += MAX_PRODUCT_PART_NUMBER_ALLOWED;
      finalProductsListLength -= MAX_PRODUCT_PART_NUMBER_ALLOWED;
    }
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        title: 'An error occurred while calling SFCC API in BundleProduct',
        componentName: 'BundleProduct.saga',
        methodName: 'callSFCCInventoryAPI',
        res,
      },
    });
  }
  return taskData;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function* updateUnbxdDataWithSFCCInventoryData(res, taskDataObject, isPJPlannerExperienceEnabled) {
  try {
    const taskData = yield join(taskDataObject);
    const isExtendedInventoryPromoMessagingEnabled = yield select(
      getIsExtendedInventoryPromoMessagingEnabled
    );
    const products = res && JSON.parse(JSON.stringify(res));
    const productsLength = getProductsLength(products);
    const clonedProducts = products && JSON.parse(JSON.stringify(products));
    let data = [];
    for (let i = 0; i < taskData.length; i += 1) {
      const apiResponse = yield join(taskData[i]);
      if (apiResponse) {
        data = [...data, ...apiResponse];
      }
    }

    for (let i = 0; i < productsLength; i += 1) {
      const isGiftCardProduct = products[i]?.products?.isGiftCard;
      const isAccessoryProduct = !isEmpty(
        products[i]?.products?.accessoriesData?.accessoriesProductID
      );
      const shouldUpdateInventoryAttributesCheck =
        isExtendedInventoryPromoMessagingEnabled && !isGiftCardProduct && !isAccessoryProduct;

      const updatedColorFitsSizesMap = updateColorFitsSizesMapWithSFCCInventoryData({
        colorFitsSizesMap: products[i]?.products?.colorFitsSizesMap,
        data,
        shouldUpdateInventoryAttributesCheck,
      });

      const updatedAllProductId = updateRawDataWithSFCCInventoryData(
        products[i]?.products?.allProductId,
        data
      );
      const updatedMultiPackAllColor = updateRawDataWithSFCCInventoryData(
        products[i]?.products?.getMultiPackAllColor,
        data
      );

      const updatedCategoryIdList = updateRawDataWithSFCCInventoryData(
        products[i]?.products?.categoryIdList,
        data,
        false,
        true
      );

      const updatedNewCategoryIdList = updateRawDataWithSFCCInventoryData(
        products[i]?.products?.newCategoryIdList,
        data,
        true,
        false
      );
      const clonedProduct = clonedProducts[i].products;
      if (products[i]?.products?.getNewMultiPackAllColor?.length) {
        const updatedNewMultiPackAllColor = updateRawDataWithSFCCInventoryData(
          products[i]?.products?.getNewMultiPackAllColor,
          data,
          false,
          false
        );
        const updatedNewPartNumber = updateRawDataWithSFCCInventoryData(
          products[i]?.newPartNumber,
          data,
          false,
          true
        );
        clonedProduct.getNewMultiPackAllColor = updatedNewMultiPackAllColor;
        clonedProduct.newPartNumber = updatedNewPartNumber;
      }

      if (shouldUpdateInventoryAttributesCheck) {
        const currentProductInventoryData = data.find(
          (item) => item.id === products[i]?.products?.productId
        );

        if (currentProductInventoryData?.price_max) {
          clonedProduct.highOfferPrice = currentProductInventoryData?.price_max;
        }

        if (currentProductInventoryData?.price) {
          clonedProduct.offerPrice = currentProductInventoryData?.price;
        }
      }

      clonedProduct.colorFitsSizesMap = updatedColorFitsSizesMap;
      clonedProduct.allProductId = updatedAllProductId;
      clonedProduct.getMultiPackAllColor = updatedMultiPackAllColor;
      clonedProduct.categoryIdList = updatedCategoryIdList;
      clonedProduct.newCategoryIdList = updatedNewCategoryIdList;
      clonedProducts[i].products = clonedProduct;
    }
    yield put(setBundleDetails(clonedProducts));

    if (isPJPlannerExperienceEnabled) {
      yield getDepartmentBundleProducts(clonedProducts);
    }
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        title: 'An error occurred while updating unbxd data with SFCC data in BundleProduct',
        componentName: 'BundleProduct.saga',
        methodName: 'updateUnbxdDataWithSFCCInventoryData',
        res,
        taskDataObject,
      },
    });
  }
}

export function* fetchBundleProductDetail(args) {
  const {
    payload: {
      productId,
      url,
      otherBrand,
      apiConfig,
      dataAvailable,
      isPDPInventoryCheckEnabled,
      resolve,
    },
  } = args;
  try {
    yield put(
      setSlpLoadingState({
        alternateBrandName: '',
      })
    );
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const pageName = 'pdp';
    const state = yield select();
    if (!dataAvailable) {
      yield put(setProductDetails({ product: {} }));
      yield put(setLoadingState({ isLoading: true }));
    }
    const isPdpServiceEnabled = yield select(getIsPdpServiceEnabled);
    const enablePDPServiceABTest = yield select(getABTestPdpService);
    const productDetail = yield call(
      getProductInfoById,
      xappURLConfig,
      productId,
      state,
      null,
      true,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      isPdpServiceEnabled,
      enablePDPServiceABTest,
      null,
      otherBrand
    );
    const { primaryCategoryId } = productDetail;
    let newBreadCrumbsArray = yield select(newBreadcrumbs(primaryCategoryId?.value));
    newBreadCrumbsArray = processHelperUtil.processBreadcrumbs(newBreadCrumbsArray);
    yield put(setProductDetails({ ...productDetail, breadCrumbs: newBreadCrumbsArray }));
    const bundledProducts = productDetail.product.bundleProducts;

    const bundleDetails = yield call(
      getBundleProductsDetails,
      xappURLConfig,
      { bundleProducts: bundledProducts },
      state,
      isPdpServiceEnabled,
      enablePDPServiceABTest,
      otherBrand
    );
    let taskDataObject;
    if (isPDPInventoryCheckEnabled) {
      taskDataObject = yield fork(callSFCCInventoryAPI, bundleDetails);
    }

    yield put(setBundleDetails([...bundleDetails]));
    yield call(getDefaultWishList);
    yield put(setLoadingState({ isLoading: false }));

    const isPJPlannerExperienceEnabled = yield select(getIsPJPlannerExperienceEnabled);
    if (isPJPlannerExperienceEnabled) {
      yield getDepartmentBundleProducts(bundleDetails);
    }

    if (bundledProducts && bundledProducts.length > 0) {
      const category = bundledProducts[0] && bundledProducts[0].category;
      const { layout, modules } = yield call(layoutResolver, { category, pageName, apiConfig });
      yield put(loadLayoutData(layout, pageName));
      yield put(loadModulesData(modules));
      yield put(setPageUrl(url));
      resolveCall(resolve, true);
    } else {
      resolveCall(resolve, false);
    }

    if (isPDPInventoryCheckEnabled && bundleDetails && taskDataObject) {
      yield updateUnbxdDataWithSFCCInventoryData(
        bundleDetails,
        taskDataObject,
        isPJPlannerExperienceEnabled
      );
    }
  } catch (err) {
    resolveCall(resolve, false);
    logger.error('error: ', err);
    yield put(setLoadingState({ isLoading: false }));
  }
}

function* watchAddedToBagAction(args) {
  try {
    const { payload } = args;
    const { debounce } = payload || {};
    const isBundle = yield select(getIsBundle);
    const isOutfit = yield select(getIsOutfit);
    const basketId = getBasketId();
    if (!basketId) return;
    if (debounce) {
      yield delay(debounce);
    }
    if (isBundle || isOutfit) {
      const {
        cart: { productItems },
      } = yield call(getInYourBagItems, true);
      yield put(setInYourBagCount(productItems));
    }
  } catch (error) {
    logger.error({
      error: `Error in BundleProductSaga - watchAddedToBagAction catch - ${error}`,
      extraData: {
        component: 'BundleProductSaga - watchAddedToBagAction',
      },
    });
  }
}

function* fetchSelectedFitProductDetails(args) {
  try {
    const { payload } = args;
    const { prodId, bundleItemIndex, otherBrand, generalProductId } = payload;
    const state = yield select();
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const isPdpServiceEnabled = yield select(getIsPdpServiceEnabled);
    const enablePDPServiceABTest = yield select(getABTestPdpService);
    const bundleDetails = yield call(
      getBundleProductsDetails,
      xappURLConfig,
      { bundleProducts: [prodId], bundleItemIndex, newFitsEnabled: true },
      state,
      isPdpServiceEnabled,
      enablePDPServiceABTest,
      otherBrand
    );

    yield put(
      updateBundleProductDetails({
        product: {
          ...bundleDetails[0],
          generalProductId,
        },
        index: bundleItemIndex,
      })
    );
  } catch (error) {
    logger.error({
      error: `Error in BundleProductSaga - fetchSelectedFitProductDetails catch - ${error}`,
      extraData: {
        component: 'BundleProductSaga - fetchSelectedFitProductDetails',
      },
    });
  }
}

function* BundleProductSaga() {
  yield takeLatest(BUNDLEPRODUCT_CONSTANTS.FETCH_BUNDLE_DETAILS, fetchBundleProductDetail);
  yield takeLatest(BUNDLEPRODUCT_CONSTANTS.GET_IN_YOUR_BAG_PRODUCT_COUNT, watchAddedToBagAction);
  yield takeLatest(BUNDLEPRODUCT_CONSTANTS.CALL_ITEMS_IN_BAG, watchAddedToBagAction);
  yield takeLatest(CARTPAGE_CONSTANTS.REMOVE_CART_ITEM, watchAddedToBagAction);
  yield takeLatest(
    BUNDLEPRODUCT_CONSTANTS.FETCH_SELECTED_FIT_PRODUCT,
    fetchSelectedFitProductDetails
  );
}

export default BundleProductSaga;
